import { Input } from "@/components/ui/input";
import { useCustomAddressStore } from "../stores/customAddressStore";
import type { CustomAddressDto } from "../types/customAddress.types";

const COLUMNS = [
  { key: "fullAddr", label: "지번 주소" },
  { key: "placeName", label: "장소명" },
  { key: "road", label: "도로명" },
  { key: "coord", label: "좌표 (위도, 경도)" },
  { key: "keywords", label: "검색 키워드" },
  { key: "updatedAt", label: "수정일" },
] as const;

type ColumnKey = (typeof COLUMNS)[number]["key"];

interface CustomAddressTableProps {
  data: CustomAddressDto[] | undefined;
  isLoading: boolean;
  error?: Error | null;
}

const formatDate = (value: string) => (value ? value.substring(0, 10) : "-");

export function CustomAddressTable({ data, isLoading, error }: CustomAddressTableProps) {
  const keyword = useCustomAddressStore((s) => s.keyword);
  const setKeyword = useCustomAddressStore((s) => s.setKeyword);
  const openDrawerForEdit = useCustomAddressStore((s) => s.openDrawerForEdit);
  const items = data ?? [];

  const renderCell = (row: CustomAddressDto, key: ColumnKey) => {
    switch (key) {
      case "fullAddr":
        return <span className="font-medium text-blue-600">{row.fullAddr}</span>;
      case "placeName":
        return row.placeName || "-";
      case "road":
        return [row.roadName, row.buildingNo].filter(Boolean).join(" ") || "-";
      case "coord":
        return (
          <span className="tabular-nums text-gray-600">
            {row.latitude.toFixed(6)}, {row.longitude.toFixed(6)}
          </span>
        );
      case "keywords":
        return row.keywords.length === 0 ? (
          "-"
        ) : (
          <span className="flex flex-wrap gap-1">
            {row.keywords.map((keyword) => (
              <span
                key={keyword}
                className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700"
              >
                {keyword}
              </span>
            ))}
          </span>
        );
      case "updatedAt":
        return formatDate(row.updatedAt);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex shrink-0 items-center gap-3 border-b border-gray-200 bg-white p-3">
        <span className="text-base font-medium text-gray-900">보완 주소 목록</span>
        <span className="text-xs text-gray-500">총 {items.length}건</span>
        <Input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="주소·장소명·키워드 검색"
          className="ml-auto h-8 w-72 text-xs"
        />
      </div>

      <div className="flex-1 overflow-auto bg-white">
        <table className="w-full">
          <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50">
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className="h-10 px-4 py-3 text-left text-xs font-medium uppercase text-gray-500"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {isLoading ? (
              <tr>
                <td colSpan={COLUMNS.length} className="h-32 text-center text-xs text-gray-400">
                  조회 중...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={COLUMNS.length} className="h-32 px-4 text-center text-xs text-red-600">
                  <p className="font-medium">보완 주소 목록을 불러오지 못했습니다</p>
                  <p className="mt-1 text-gray-500">{error.message}</p>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="h-32 text-center text-xs text-gray-400">
                  {keyword.trim() ? "검색 결과가 없습니다" : "등록된 보완 주소가 없습니다"}
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr
                  key={row.caIdx}
                  className="h-10 cursor-pointer hover:bg-gray-50"
                  onDoubleClick={() => openDrawerForEdit(row.caIdx)}
                >
                  {COLUMNS.map((col) => (
                    <td key={col.key} className="px-4 py-2 text-xs text-gray-700">
                      {renderCell(row, col.key)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
        더블클릭으로 수정 · 저장 즉시 ULA 주소 검색에 반영됩니다(캐시 무관)
      </div>
    </div>
  );
}
